import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import { eventApi } from '../services/api';
import {
  setupTracking,
  storePendingConversion,
  TrackingConfig,
  TrackingScript,
} from '../tracking/tracking';
import { useTranslation } from '../i18n/TranslationContext';
import LanguageSelector from './LanguageSelector';

interface SubscriptionDetails {
  subscription_id: number;
  amount: string | number;
  group_size?: number;
  payment_status: string;
  customer: { name: string; lastname: string; email: string };
  event: {
    id: number;
    name: string;
    bank_account_id: number | null;
    paypal_store_id: string | null;
    stripe_store_id: string | null;
    multilingual_enabled?: boolean;
  };
  carta_docente_allowed: boolean;
  tracking_config?: TrackingConfig | null;
  tracking_scripts_list?: TrackingScript[];
}

const formatAmount = (a: string | number) => `€${Number(a).toFixed(2)}`;

const PaySubscriptionPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, setForcedDefault } = useTranslation();

  const statusLabel = (s: string) => ({
    PENDING: t('stati.pending', 'In attesa di pagamento'),
    PENDING_BANK_TRANSFER: t('stati.pending_bonifico', 'In attesa del bonifico'),
    PENDING_CARTA_DOCENTE: t('stati.pending_cd', 'In attesa Carta del Docente'),
    PROCESSING: t('stati.processing', 'In elaborazione'),
    FAILED: t('stati.failed', 'Pagamento non riuscito'),
    PAYED: t('stati.payed', 'Pagata'),
    CANCELLED: t('stati.cancelled', 'Annullata'),
  } as Record<string, string>)[s] ?? s;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [cartaDocenteSent, setCartaDocenteSent] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!token) {
        setError(t('paga.token_mancante', 'Token mancante'));
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await eventApi.getSubscriptionByToken(token);
        if (res?.success) {
          setSubscription(res.data);
          setupTracking(res.data?.tracking_config ?? null, res.data?.tracking_scripts_list ?? []);
          setForcedDefault(!res.data?.event?.multilingual_enabled);
        } else {
          setError(res?.error || t('paga.non_trovata', 'Iscrizione non trovata'));
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || t('paga.err_caricamento', 'Errore caricamento iscrizione'));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  const handlePay = async (method: string) => {
    if (!subscription) return;
    try {
      setSubmitting(method);
      setError('');
      const res = await eventApi.processPayment(subscription.subscription_id, method);
      if (res?.redirect_url) {
        storePendingConversion({
          value: Number(subscription.amount),
          eventName: subscription.event?.name ?? '',
          config: subscription.tracking_config ?? null,
          scripts: subscription.tracking_scripts_list ?? [],
        });
        window.location.href = res.redirect_url;
        return;
      }
      if (res?.bank_details) {
        setBankDetails(res.bank_details);
      } else if (res?.payment_method === 'carta_docente') {
        setCartaDocenteSent(true);
      } else {
        setError(res?.message || t('evento.err_risposta', 'Risposta di pagamento non valida dal server'));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || t('paga.err_avvio', 'Errore avvio pagamento'));
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      </Container>
    );
  }

  const isPayable = subscription
    && subscription.payment_status !== 'PAYED'
    && subscription.payment_status !== 'CANCELLED';

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {!!subscription?.event?.multilingual_enabled && <LanguageSelector sx={{ mb: 1.5 }} />}
      <Paper elevation={2} sx={{ p: 4 }}>
        {error && !subscription && (
          <Alert severity="error">{error}</Alert>
        )}

        {subscription && (
          <>
            <Typography variant="h5" gutterBottom>
              {t('paga.titolo', 'Pagamento iscrizione')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {subscription.event?.name}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 1 }}>
              <Typography variant="body2"><strong>{t('paga.intestatario', 'Intestatario')}:</strong> {subscription.customer.name} {subscription.customer.lastname}</Typography>
            </Box>
            {(subscription.group_size ?? 1) > 1 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2"><strong>{t('gruppo.titolo', 'Iscrizione di gruppo')}:</strong> {t('gruppo.n_persone', '{{n}} persone', { n: subscription.group_size ?? 0 })}</Typography>
              </Box>
            )}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2"><strong>{t('bonifico.importo', 'Importo')}:</strong> {formatAmount(subscription.amount)}{(subscription.group_size ?? 1) > 1 ? ` ${t('paga.totale_gruppo', '(totale gruppo)')}` : ''}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>{t('paga.stato', 'Stato')}:</strong> {statusLabel(subscription.payment_status)}</Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {!isPayable ? (
              <Alert severity={subscription.payment_status === 'PAYED' ? 'success' : 'warning'}>
                {subscription.payment_status === 'PAYED'
                  ? t('paga.gia_pagata', 'Questa iscrizione è già stata pagata.')
                  : t('paga.annullata', 'Questa iscrizione è stata annullata.')}
              </Alert>
            ) : bankDetails ? (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('paga.bonifico_testo', 'Per completare la registrazione, effettua un bonifico con i seguenti dati (li abbiamo inviati anche via email):')}
                </Typography>
                <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                  {bankDetails.beneficiary && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>{t('bonifico.beneficiario', 'Beneficiario')}:</strong> {bankDetails.beneficiary}</Typography>
                  )}
                  {bankDetails.iban && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>{t('bonifico.iban', 'IBAN')}:</strong> {bankDetails.iban}</Typography>
                  )}
                  {bankDetails.bic && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>BIC/SWIFT:</strong> {bankDetails.bic}</Typography>
                  )}
                  {bankDetails.bank_name && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>{t('bonifico.banca', 'Banca')}:</strong> {bankDetails.bank_name}</Typography>
                  )}
                  {bankDetails.causale && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>{t('bonifico.causale', 'Causale')}:</strong> {bankDetails.causale}</Typography>
                  )}
                  {bankDetails.amount && (
                    <Typography variant="body2"><strong>{t('bonifico.importo', 'Importo')}:</strong> {bankDetails.amount}</Typography>
                  )}
                </Box>
              </>
            ) : cartaDocenteSent ? (
              <Alert severity="success">
                {t('paga.cd_inviata', "Ti abbiamo inviato un'email con le istruzioni per completare il pagamento con la Carta del Docente. L'iscrizione sarà confermata dopo la verifica del buono.")}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  {t('paga.scegli', 'Scegli come completare il pagamento:')}
                </Typography>
                {subscription.event?.paypal_store_id && (
                  <Button variant="contained" size="large" fullWidth disabled={!!submitting} onClick={() => handlePay('paypal')}>
                    {submitting === 'paypal' ? <CircularProgress size={22} /> : t('paga.paypal', 'Paga con PayPal {{importo}}', { importo: formatAmount(subscription.amount) })}
                  </Button>
                )}
                {subscription.event?.stripe_store_id && (
                  <Button variant="contained" size="large" fullWidth disabled={!!submitting} onClick={() => handlePay('stripe')}>
                    {submitting === 'stripe' ? <CircularProgress size={22} /> : t('paga.stripe', 'Paga con carta / Stripe {{importo}}', { importo: formatAmount(subscription.amount) })}
                  </Button>
                )}
                {subscription.event?.bank_account_id && (
                  <Button variant="outlined" size="large" fullWidth disabled={!!submitting} onClick={() => handlePay('bonifico')}>
                    {submitting === 'bonifico' ? <CircularProgress size={22} /> : t('paga.bonifico', 'Paga con bonifico bancario')}
                  </Button>
                )}
                {subscription.carta_docente_allowed && (
                  <Button variant="outlined" size="large" fullWidth disabled={!!submitting} onClick={() => handlePay('carta_docente')}>
                    {submitting === 'carta_docente' ? <CircularProgress size={22} /> : t('paga.cd', 'Paga con Carta del Docente')}
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PaySubscriptionPage;
